import { createClient } from '@supabase/supabase-js';

// NOTE: In a real environment, these would come from import.meta.env or process.env
const supabaseUrl =
  ((import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL as
    | string
    | undefined) || 'https://xyzcompany.supabase.co';
const supabaseKey =
  ((import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_ANON_KEY as
    | string
    | undefined) || 'public-anon-key';
const supabaseRoleKey =
  ((import.meta as { env?: Record<string, string> }).env?.VITE_SUPABASE_ROLE_KEY as
    | string
    | undefined) || 'service-role-key';

// Optimized fetch untuk Cloudflare - dengan cache headers dan connection pooling
const customFetch = (url: RequestInfo | URL, options: RequestInit = {}) => {
  const controller = new AbortController();
  // Reduced timeout untuk auth requests (lebih cepat fail jika ada masalah)
  // Cloudflare edge caching bisa membantu mengurangi latency
  const isAuthRequest =
    typeof url === 'string' && (url.includes('/auth/v1') || url.includes('/auth/v1/token'));
  const timeout = isAuthRequest ? 10000 : 30000; // 10s untuk auth, 30s untuk lainnya
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Merge with existing signal if present
  const existingSignal = options.signal;
  if (existingSignal) {
    existingSignal.addEventListener('abort', () => controller.abort());
  }

  // Optimize headers untuk Cloudflare
  const headers = new Headers(options.headers);

  // Add Accept header untuk memastikan response format yang benar
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  // Add Content-Type for requests that have body only when it's a JSON/text payload.
  // Do NOT set Content-Type for binary bodies (FormData/Blob/ArrayBuffer/Stream)
  if (options.body && !headers.has('Content-Type')) {
    const body = options.body as unknown;
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    const isBlob = typeof Blob !== 'undefined' && body instanceof Blob;
    const isArrayBuffer =
      body instanceof ArrayBuffer || (ArrayBuffer.isView && ArrayBuffer.isView(body));
    const isStream =
      body &&
      (typeof (body as { getReader?: unknown }).getReader === 'function' ||
        typeof (body as { pipe?: unknown }).pipe === 'function');

    if (!isFormData && !isBlob && !isArrayBuffer && !isStream) {
      // Safe to assume JSON/text body
      headers.set('Content-Type', 'application/json');
    }
  }

  // Add cache control untuk non-auth requests (Cloudflare akan cache)
  if (!isAuthRequest) {
    headers.set('Cache-Control', 'no-cache');
  }

  // Remove potentially problematic headers that might cause 406 errors
  headers.delete('Range');
  headers.delete('If-Range');
  // Do NOT delete 'Prefer' header — Supabase uses it (e.g. 'return=representation')
  // which is required for `.insert().select()` to return the inserted rows.

  // Avoid setting forbidden headers like User-Agent from browser
  // (browsers disallow or ignore User-Agent and it can trigger CORS preflight issues)

  return fetch(url, {
    ...options,
    headers,
    signal: controller.signal,
    // Keepalive untuk connection reuse (mengurangi latency Cloudflare)
    keepalive: true,
  })
    .finally(() => clearTimeout(timeoutId))
    .catch((error: unknown) => {
      const err = error as { name?: string };
      if (err.name === 'AbortError') {
        const timeoutError = new Error(
          'Request timeout. Server sedang lambat, silakan coba lagi.',
        ) as Error & { status?: number };
        timeoutError.name = 'TimeoutError';
        timeoutError.status = 504;
        throw timeoutError;
      }
      throw error;
    })
    .then(async (response) => {
      // Handle 406 Not Acceptable errors specifically
      if (response.status === 406) {
        console.warn('406 Not Acceptable error for URL:', url);
        console.warn('Request headers:', Object.fromEntries(Array.from(headers.entries())));
        console.warn(
          'Response headers:',
          Object.fromEntries(Array.from(response.headers.entries())),
        );

        // Try to get response text for debugging
        const responseText = await response.text();
        console.warn('Response body:', responseText);

        const notAcceptableError = new Error(
          'Request tidak dapat diterima oleh server. Mungkin ada masalah dengan format request.',
        ) as Error & { status?: number; originalResponse?: string };
        notAcceptableError.status = 406;
        notAcceptableError.originalResponse = responseText;
        throw notAcceptableError;
      }
      return response;
    });
};

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    // Allow for some clock skew (1 minute) to prevent "issued in the future" errors
    // This is helpful if the client's system clock is slightly behind the server
    debug: ((import.meta as { env?: { DEV?: boolean } }).env?.DEV as boolean | undefined) || false, // Enable debug in development to help troubleshoot auth issues
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Increase flow type timeout for signup
    flowType: 'pkce',
  },
  global: (() => {
    // Use custom fetch with longer timeout only on server; let browser use native fetch
    // (Avoids creating a second fetch wrapper in the browser and prevents
    // duplicate GoTrue/transport side-effects.)
    const cfg: { headers: { 'X-Client-Info': string }; fetch?: typeof customFetch } = {
      headers: { 'X-Client-Info': 'web-ypimanawa' },
    };
    if (typeof window === 'undefined') {
      cfg.fetch = customFetch;
    }
    return cfg;
  })(),
});

// Do NOT instantiate a service-role client in the browser — it creates a second GoTrue
// instance which triggers the "Multiple GoTrueClient instances" warning and is unsafe.
// Export a helper to create or return an admin client only when running on the server.
export const getSupabaseAdmin = () => {
  if (typeof window !== 'undefined') {
    // In browser context: explicitly avoid creating a service-role client
    // Return null so callers must opt-in to server-only usage.
    return null;
  }

  // In a server/node context we can safely create a service-role client.
  return createClient(supabaseUrl, supabaseRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
  });
};
